import React from "react";
import {
    RESET_GANTT,
    SET_LOCK_FILTER,
    SET_RESCHEDULE_LINE_UUID,
    SET_RESCHEDULE_ORDER_BUNDLE,
    SET_RESCHEDULE_ORDER_UUID,
} from "../common";
import { deepClone } from "@/lib/util";
import local_settings, { FIELD_SHOW_STOCK_REQUIREMENTS, FIELD_SHOW_UNSCHEDULED_ORDERS } from "@/lib/localSettings";
import { ORDER_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";

import type { PayloadAction } from "@/store/types";
import type { IPlanLine, IPlanExplanation, IPlanWarning } from "@/lib/backend/manufacturing2.generated.types";
import type { OrderHighlights } from "../linesOrders";
import type {
    ResetGanttAction,
    SetLockFilter,
    SetRescheduleLineAction,
    SetRescheduleOrderAction,
    SetRescheduleOrderBundleAction,
} from "../common";
import type { PlanningDetailsViewsType } from "../../planningDetails/common";
import type { SetShowValidLinesState } from "../common";
import type { RecommendPlanParamsAndCriteria } from "./recommendPlan";
import { LinesOrdersMaps } from "../linesOrders/mappers";

export const PLANNING_DETAILS_VIEW_STOCK_REQUIREMENTS = "stock_requirements";
export const SET_SHOW_VALID_LINES = "set_show_valid_lines";
export const RESET_VALID_LINES = "reset_valid_lines";
export const SET_RECOMMEND_PLAN_RUNNING = "recommend_plan_running";
export const SET_RECOMMEND_PLAN_LINE_PLANS = "line_plans";
export const SET_RECOMMEND_PLAN_MODAL = "recomend_plan_modal";
export const SET_RECOMMEND_PLAN_ERROR = "set_recommend_plan_error";
export const SET_REPORT_TYPE_TITLE_ACTION_NAME = "report_version_type";
export const HIGHLIGHT_SHIFT_LINE_ACTION_NAME = "highlight_shift_line";
export const SHOW_UNSCHEDULED_ORDERS = "show_unscheduled_orders";
export const SET_ADVANCED_ORDER_MENU = "advanced_order_menu";
export const RESET_ADVANCED_ORDER_MENU = "reset_advanced_order_menu";
export const SET_ERROR = "error";
export const RESET_CURSOR = "reset_cursor";
export const SET_SHOW_EXTRA_LINES = "set_show_extra_lines";
export const SET_HIGHLIGHTED_MOVE_ORDER = "set_highlighted_move_order";
export const SET_CAMPAIGN_REFERENCE_KEY = "set_campaign_reference_key";
export const SET_IS_CTRL_DOWN = "set_ctrl_down";
export const SET_RECOMMEND_EXPLANATIONS = "set_recommend_explanations";
export const RESET_DRAGGED_ORDER = "reset_dragged_order";
export const SET_PLANNING_DETAILS_SELECTED_VIEW = "planning_details_selected_view";
export const SET_SHOW_STOCK_REQUIREMENTS = "set_show_stock_requirements";
export const RESET_FILTERS = "reset_filters";
export const SET_RECOMMEND_WARNINGS = "set_recommend_warnings";
export const SET_HIGHLIGHTED_ORDERS = "set_highlighted_order";
export const SHOW_PARENT_CHUNK_RELATIONSHIP = "show_parent_chunk_relationship";
export const SHOW_EDIT_COMMENT_MODAL = "show_edit_comment_modal";
export const SET_LAST_CLICKED_ORDER_UUID = "set_last_clicked_order_uuid";
export const TOGGLE_MULTISELECT_ORDERS = "set_multiselect_order_uuids";
export const CLEAR_MULTISELECT_ORDERS = "clear_multiselect_order_uuids";

export type SetReportTypeTitleActionData = {
    report_version_type: "initial" | "version" | "current";
    report_version_title: string;
};

export type SetRecommendPlanModalActionData = {
    show_recommend_param_dialog: boolean;
    recommend_plan_params?: RecommendPlanParamsAndCriteria;
};

export type RecommendPlanState = {
    show_recommend_param_dialog: boolean;
    recommend_plan_params: RecommendPlanParamsAndCriteria | null;
    recommend_running: boolean;
    line_plans: IPlanLine[];
    error: string | null;
    explanations: IPlanExplanation[];
    warnings: IPlanWarning[];
};

export type HighlightShiftLineActionData = {
    order_uuid: string | null;
    highlighted_shift_number: number | null;
    highlighted_line_index: number | null;
    highlighted_date_index: number | null;
    highlighted_date_ts: number | null;
    is_unscheduled?: boolean;
};

export type AdvancedOrderMenuState = {
    order_uuid: string | null;
    click_target: Element | typeof undefined | null;
    event: React.MouseEvent | null;
};

export type ToggleMultiselectOrdersActionData = {
    uuids: string[];
    toggle?: boolean;
};

const advanced_order_menu_initial_state = {
    order_uuid: null,
    click_target: null,
    event: null,
};

export type HighlightedOrders = {
    highlighted_orders_map: Map<string, OrderHighlights> | null;
};

export type ResetFiltersAction = PayloadAction<typeof RESET_FILTERS, typeof undefined>;
export type SetShowValidLinesAction = PayloadAction<typeof SET_SHOW_VALID_LINES, SetShowValidLinesState>;
export type ResetValidLinesAction = PayloadAction<typeof RESET_VALID_LINES, typeof undefined>;
export type SetRecommendPlanRunningAction = PayloadAction<typeof SET_RECOMMEND_PLAN_RUNNING, boolean>;
export type SetRecommendPlanLinePlans = PayloadAction<typeof SET_RECOMMEND_PLAN_LINE_PLANS, RecommendPlanState>;
export type SetRecommendPlanErrorAction = PayloadAction<typeof SET_RECOMMEND_PLAN_ERROR, string | null>;
export type SetRecommendPlanModalAction = PayloadAction<
    typeof SET_RECOMMEND_PLAN_MODAL,
    SetRecommendPlanModalActionData
>;
export type SetReportTypeTitleAction = PayloadAction<
    typeof SET_REPORT_TYPE_TITLE_ACTION_NAME,
    SetReportTypeTitleActionData
>;
export type HighlightShiftLineAction = PayloadAction<
    typeof HIGHLIGHT_SHIFT_LINE_ACTION_NAME,
    HighlightShiftLineActionData
>;
export type ShowUnscheduledOrdersAction = PayloadAction<typeof SHOW_UNSCHEDULED_ORDERS, boolean>;
export type SetErrorAction = PayloadAction<typeof SET_ERROR, string | null>;
export type SetAdvancedOrderMenuAction = PayloadAction<typeof SET_ADVANCED_ORDER_MENU, AdvancedOrderMenuState>;
export type ResetAdvancedOrderMenu = PayloadAction<typeof RESET_ADVANCED_ORDER_MENU, typeof undefined>;
export type CursorState = HighlightShiftLineActionData;
export type ResetCursorAction = PayloadAction<typeof RESET_CURSOR, typeof undefined>;
export type SetShowExtraLines = PayloadAction<typeof SET_SHOW_EXTRA_LINES, boolean>;
export type SetHighlightedOrders = PayloadAction<typeof SET_HIGHLIGHTED_ORDERS, HighlightedOrders>;
export type SetIsCtrlDown = PayloadAction<typeof SET_IS_CTRL_DOWN, boolean>;
export type SetRecommendExplanationsAction = PayloadAction<typeof SET_RECOMMEND_EXPLANATIONS, IPlanExplanation[]>;
export type SetPlanningDetailsSelectedView = PayloadAction<
    typeof SET_PLANNING_DETAILS_SELECTED_VIEW,
    PlanningDetailsViewsType
>;
export type SetShowStockRequirements = PayloadAction<typeof SET_SHOW_STOCK_REQUIREMENTS, boolean>;
export type ResetDraggedOrder = PayloadAction<typeof RESET_DRAGGED_ORDER, typeof undefined>;
export type SetRecommendWarningsAction = PayloadAction<typeof SET_RECOMMEND_WARNINGS, IPlanWarning[]>;
export type SetHighlightedMoveOrder = PayloadAction<typeof SET_HIGHLIGHTED_MOVE_ORDER, string | null>;
export type ShowParentChunkRelationship = PayloadAction<typeof SHOW_PARENT_CHUNK_RELATIONSHIP, string[] | null>;
export type ShowEditCommentModal = PayloadAction<typeof SHOW_EDIT_COMMENT_MODAL, string | null>;
export type SetLastClickedOrderUuid = PayloadAction<typeof SET_LAST_CLICKED_ORDER_UUID, string | null>;
export type ToggleMultiselectOrdersAction = PayloadAction<
    typeof TOGGLE_MULTISELECT_ORDERS,
    ToggleMultiselectOrdersActionData
>;
export type ClearMultiselectOrdersAction = PayloadAction<typeof CLEAR_MULTISELECT_ORDERS, typeof undefined>;
export type SetCampaignReferenceKey = PayloadAction<typeof SET_CAMPAIGN_REFERENCE_KEY, string | null>;

export type PlanningTableState = {
    should_scroll: boolean;
    show_valid_lines: SetShowValidLinesState;
    recommend_plan: RecommendPlanState;
    report_version_type: "initial" | "current" | "version";
    report_version_title: string | null;
    cursor: CursorState | null;
    max_line_label_length: number;
    show_unscheduled_orders: boolean;
    error: string | null;
    advanced_order_menu: AdvancedOrderMenuState;
    show_extra_lines: boolean;
    highlighted_orders: HighlightedOrders;
    is_ctrl_down: boolean;
    refresh_at: number | null;
    selected_planning_details_view: PlanningDetailsViewsType | null;
    show_stock_requirements: boolean;
    highlighted_move_order_uuid: string | null;
    campaign_reference_key: string | null;
    show_parent_chunk_relationship_uuids: string[] | null;
    show_edit_comment_modal_order_uuid: string | null;
    last_clicked_order_uuid: string | null;
    multiselect_order_uuids: string[] | null;
};

const recommend_plan_initial_state: RecommendPlanState = {
    show_recommend_param_dialog: false,
    recommend_plan_params: null,
    recommend_running: false,
    line_plans: [],
    error: null,
    explanations: [],
    warnings: [],
};

const show_valid_lines_initial_state: SetShowValidLinesState = {
    valid_lines: null,
    valid_lines_map: null,
    valid_orders_map: null,
};

const highlighted_orders_initial_state: HighlightedOrders = {
    highlighted_orders_map: null,
};

export const planning_table_initial_state: PlanningTableState = {
    should_scroll: false,
    show_valid_lines: show_valid_lines_initial_state,
    recommend_plan: recommend_plan_initial_state,
    report_version_type: "initial",
    report_version_title: null,
    cursor: null,
    show_unscheduled_orders: true,
    max_line_label_length: 0,
    error: null,
    advanced_order_menu: { ...advanced_order_menu_initial_state },
    show_extra_lines: true,
    highlighted_orders: highlighted_orders_initial_state,
    is_ctrl_down: false,
    refresh_at: null,
    selected_planning_details_view: null,
    show_stock_requirements: false,
    highlighted_move_order_uuid: null,
    show_parent_chunk_relationship_uuids: null,
    show_edit_comment_modal_order_uuid: null,
    last_clicked_order_uuid: null,
    multiselect_order_uuids: null,
    campaign_reference_key: null,
};

export type PlanningTableActionsTypes =
    | SetShowValidLinesAction
    | SetRecommendPlanRunningAction
    | SetRecommendPlanLinePlans
    | SetRecommendPlanErrorAction
    | SetReportTypeTitleAction
    | SetRecommendPlanModalAction
    | HighlightShiftLineAction
    | ShowUnscheduledOrdersAction
    | ResetValidLinesAction
    | SetErrorAction
    | SetAdvancedOrderMenuAction
    | ResetAdvancedOrderMenu
    | ResetCursorAction
    | ResetGanttAction
    | SetShowExtraLines
    | SetHighlightedOrders
    | SetIsCtrlDown
    | SetRecommendExplanationsAction
    | SetShowStockRequirements
    | ResetFiltersAction
    | SetPlanningDetailsSelectedView
    | ResetDraggedOrder
    | SetRecommendWarningsAction
    | SetHighlightedMoveOrder
    | SetCampaignReferenceKey
    | ShowParentChunkRelationship
    | ShowEditCommentModal
    | SetLastClickedOrderUuid
    | SetLockFilter
    | SetRescheduleLineAction
    | SetRescheduleOrderAction
    | SetRescheduleOrderBundleAction
    | ToggleMultiselectOrdersAction
    | ClearMultiselectOrdersAction;

const getInitialState = (): PlanningTableState => {
    const initial_state = deepClone(planning_table_initial_state);
    initial_state.show_stock_requirements = local_settings.getItem(FIELD_SHOW_STOCK_REQUIREMENTS) === "true";
    initial_state.show_unscheduled_orders = local_settings.getItem(FIELD_SHOW_UNSCHEDULED_ORDERS) === "true";
    return initial_state;
};

export const planningTableReducer = (
    state: PlanningTableState,
    action: PlanningTableActionsTypes
): PlanningTableState => {
    if (!state) {
        state = getInitialState();
    }
    let new_state = state;

    if (action.type === RESET_GANTT) {
        return getInitialState();
    } else if (action.type === SET_SHOW_VALID_LINES) {
        new_state.show_valid_lines = action.data;
        new_state = { ...new_state };
    } else if (action.type === RESET_VALID_LINES) {
        new_state.show_valid_lines = show_valid_lines_initial_state;
        new_state = { ...new_state };
    } else if (action.type === SET_RECOMMEND_PLAN_RUNNING) {
        const recommend_plan_state = new_state.recommend_plan;
        recommend_plan_state.recommend_running = action.data;
        if (action.data) {
            recommend_plan_state.error = null;
        }
        new_state.recommend_plan = { ...recommend_plan_state };
        new_state = { ...new_state };
    } else if (action.type === SET_RECOMMEND_PLAN_ERROR) {
        new_state = {
            ...new_state,
            recommend_plan: {
                ...new_state.recommend_plan,
                error: action.data,
            },
        };
    } else if (action.type === SET_RECOMMEND_PLAN_LINE_PLANS) {
        new_state.recommend_plan = { ...action.data };
        new_state = { ...new_state };
    } else if (action.type === SET_RECOMMEND_PLAN_MODAL) {
        const recommend_plan_state = new_state.recommend_plan;
        recommend_plan_state.show_recommend_param_dialog = action.data.show_recommend_param_dialog;
        recommend_plan_state.recommend_plan_params = action.data.recommend_plan_params || null;
        new_state.recommend_plan = { ...recommend_plan_state };
        new_state = { ...new_state };
    } else if (action.type === SET_REPORT_TYPE_TITLE_ACTION_NAME) {
        new_state.report_version_type = action.data.report_version_type;
        new_state.report_version_title = action.data.report_version_title;
        new_state = { ...new_state };
    } else if (action.type === HIGHLIGHT_SHIFT_LINE_ACTION_NAME) {
        let cursor = new_state.cursor;
        cursor = !new_state.cursor ? action.data : { ...cursor, ...action.data };
        new_state = { ...new_state, cursor };
    } else if (action.type === SHOW_UNSCHEDULED_ORDERS) {
        local_settings.setItem(FIELD_SHOW_UNSCHEDULED_ORDERS, action.data.toString());
        new_state.show_unscheduled_orders = action.data;
        return { ...new_state };
    } else if (action.type === SET_ERROR) {
        new_state.error = action.data;
        return { ...new_state };
    } else if (action.type === SET_ADVANCED_ORDER_MENU) {
        new_state.advanced_order_menu = action.data;
        return { ...new_state };
    } else if (action.type === RESET_CURSOR) {
        new_state.cursor = null;
        return { ...new_state };
    } else if (action.type === SET_SHOW_EXTRA_LINES) {
        new_state.show_extra_lines = action.data;
        return { ...new_state };
    } else if (action.type === SET_HIGHLIGHTED_ORDERS) {
        new_state.highlighted_orders = action.data;
        return { ...new_state };
    } else if (action.type === SET_IS_CTRL_DOWN) {
        new_state.is_ctrl_down = action.data;
        return { ...new_state };
    } else if (action.type === RESET_ADVANCED_ORDER_MENU) {
        new_state.advanced_order_menu = { ...advanced_order_menu_initial_state };
        return { ...new_state };
    } else if (action.type === SET_RECOMMEND_EXPLANATIONS) {
        new_state = {
            ...new_state,
            recommend_plan: {
                ...new_state.recommend_plan,
                explanations: action.data,
            },
        };
        return new_state;
    } else if (action.type === RESET_DRAGGED_ORDER) {
        new_state.cursor = null;
        return { ...new_state };
    } else if (action.type === SET_PLANNING_DETAILS_SELECTED_VIEW) {
        new_state.selected_planning_details_view = action.data;
        return { ...new_state };
    } else if (action.type === SET_SHOW_STOCK_REQUIREMENTS) {
        local_settings.setItem(FIELD_SHOW_STOCK_REQUIREMENTS, action.data.toString());
        new_state.show_stock_requirements = action.data;
        if (new_state.show_stock_requirements) {
            new_state.selected_planning_details_view = PLANNING_DETAILS_VIEW_STOCK_REQUIREMENTS;
        }
        return { ...new_state };
    } else if (action.type === RESET_FILTERS) {
        new_state.highlighted_orders.highlighted_orders_map = null;
        new_state.last_clicked_order_uuid = null;
        new_state.highlighted_move_order_uuid = null;
        new_state.campaign_reference_key = null;
        return { ...new_state };
    } else if (action.type === SET_RECOMMEND_WARNINGS) {
        new_state = {
            ...new_state,
            recommend_plan: {
                ...new_state.recommend_plan,
                warnings: action.data,
            },
        };
        return new_state;
    } else if (action.type === SET_HIGHLIGHTED_MOVE_ORDER) {
        new_state.highlighted_move_order_uuid = action.data;
        return { ...new_state };
    } else if (action.type === SET_CAMPAIGN_REFERENCE_KEY) {
        new_state.campaign_reference_key = action.data;
        return { ...new_state };
    } else if (action.type === SHOW_PARENT_CHUNK_RELATIONSHIP) {
        new_state.show_parent_chunk_relationship_uuids = action.data;
        return { ...new_state };
    } else if (action.type === SHOW_EDIT_COMMENT_MODAL) {
        new_state.show_edit_comment_modal_order_uuid = action.data;
        return { ...new_state };
    } else if (action.type === SET_LAST_CLICKED_ORDER_UUID) {
        new_state.last_clicked_order_uuid = action.data;
        return { ...new_state };
    } else if (action.type === SET_LOCK_FILTER) {
        if (!action.data) {
            // TODO
            new_state.highlighted_orders.highlighted_orders_map = null;
            new_state.last_clicked_order_uuid = null;
        }
        return { ...new_state };
    } else if (action.type === SET_RESCHEDULE_LINE_UUID) {
        new_state.highlighted_move_order_uuid = null;
        return { ...new_state };
    } else if (action.type === SET_RESCHEDULE_ORDER_UUID) {
        new_state.highlighted_move_order_uuid = null;
        new_state.last_clicked_order_uuid = action.data;
        return { ...new_state };
    } else if (action.type === SET_RESCHEDULE_ORDER_BUNDLE) {
        new_state.last_clicked_order_uuid = action.data.order_uuid;
        if (action.data.show_valid_lines != null) {
            new_state.show_valid_lines = action.data.show_valid_lines;
        }
        new_state.selected_planning_details_view = "orders";
        new_state.highlighted_orders = action.data.highlighted_orders;
        new_state = { ...new_state };
        return new_state;
    } else if (action.type === CLEAR_MULTISELECT_ORDERS) {
        new_state.multiselect_order_uuids = null;
        return { ...new_state };
    } else if (action.type === TOGGLE_MULTISELECT_ORDERS) {
        const { uuids, toggle } = action.data;
        const multiselect_order_uuids_set: Set<string> = new Set(new_state.multiselect_order_uuids);
        for (const operation_uuid of uuids) {
            if (toggle && multiselect_order_uuids_set.has(operation_uuid)) {
                multiselect_order_uuids_set.delete(operation_uuid);
            } else {
                multiselect_order_uuids_set.add(operation_uuid);
                const operation = LinesOrdersMaps.all_orders_map.get(operation_uuid);
                if (operation !== undefined) {
                    // Handle collective order groups
                    const selected_order_id = operation.external_id;
                    const leading_order_id = ORDER_TAGS_ACCESS.leading_order(operation.tags);

                    LinesOrdersMaps.all_orders_map.forEach((op, uuid) => {
                        if (LinesOrdersMaps.unscheduled_orders.has(uuid)) {
                            return;
                        }

                        const op_leading_order_id = ORDER_TAGS_ACCESS.leading_order(op.tags);
                        if (
                            (leading_order_id !== "" &&
                                (op.external_id === leading_order_id || op_leading_order_id === leading_order_id)) ||
                            op_leading_order_id === selected_order_id
                        ) {
                            multiselect_order_uuids_set.add(uuid);
                        }
                    });
                }
            }
        }

        new_state.multiselect_order_uuids =
            multiselect_order_uuids_set.size > 0 ? Array.from(multiselect_order_uuids_set) : null;
        return { ...new_state };
    }

    return new_state;
};
